import { useEffect, useCallback, useRef } from 'react';
import { useCookieConsent } from './useCookieConsent';

const GA_MEASUREMENT_ID = 'G-N0VCRQ54CG';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export const useGoogleAnalytics = () => {
  const { preferences, isLoaded } = useCookieConsent();
  const scriptLoadedRef = useRef(false);

  // Load GA4 script dynamically
  const loadGA4Script = useCallback(() => {
    if (scriptLoadedRef.current) return;
    
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    script.id = 'ga4-script';
    
    script.onload = () => {
      // Initialisation EXACTE Google - ordre critique
      window.dataLayer = window.dataLayer || [];
      
      // Utiliser arguments (pas args) - requis par gtag.js
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag as typeof window.gtag;
      
      // OBLIGATOIRE : initialise le moteur GA
      window.gtag('js', new Date());
      
      // Config avec page_view automatique
      window.gtag('config', GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
        send_page_view: true,
      });
    };
    
    script.onerror = () => {
      console.error('[GA4] Failed to load script');
    };
    
    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  }, []);

  // Remove GA cookies
  const removeGACookies = useCallback(() => {
    const cookies = document.cookie.split(';');
    const gaCookies = cookies.filter(cookie => 
      cookie.trim().startsWith('_ga') || 
      cookie.trim().startsWith('_gid') ||
      cookie.trim().startsWith('_gat')
    );
    
    gaCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      // Remove from current domain and subdomains
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    });
  }, []);

  // Handle consent changes
  useEffect(() => {
    if (!isLoaded) return;

    if (preferences.analytics) {
      loadGA4Script();
    } else {
      removeGACookies();
      if (scriptLoadedRef.current && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    }
  }, [preferences.analytics, isLoaded, loadGA4Script, removeGACookies]);

  // Track custom events
  const trackEvent = useCallback((eventName: string, params?: Record<string, unknown>) => {
    if (!preferences.analytics || !window.gtag) return;
    window.gtag('event', eventName, params);
  }, [preferences.analytics]);

  // Track page view
  const trackPageView = useCallback((path: string, title?: string) => {
    if (!preferences.analytics || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  }, [preferences.analytics]);

  // Track CTA click with beacon to ensure event is sent before navigation
  const trackCTA = useCallback((
    action: 'cta_click_signup' | 'cta_click_calendly' | 'cta_click_login' | 'cta_click_3p_compare',
    ctaId: string,
    location: string
  ) => {
    if (!preferences.analytics || !window.gtag) return;
    window.gtag('event', action, {
      cta_id: ctaId,
      location: location,
      transport_type: 'beacon'
    });
  }, [preferences.analytics]);

  return { trackEvent, trackPageView, trackCTA, isEnabled: preferences.analytics };
};
